import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { useHistory } from 'react-router-dom';
import { CboRole } from '@cbo/shared-library';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Header from '../../components/Header/Header';
import FormFooter from '../../components/StickyFooter/FormFooter';
import ActionsDialog from '../../components/ActionsDialog/ActionsDialog';
import UnsavedFormDataPrompt from '../../components/UnsavedDataPrompt/UnsavedFormDataPrompt';
import useYupValidationResolver from '../../utils/formUtils/yupValidationResolver';
import { useUsers } from '../../contexts/userContext';
import isPermitted from '../../lib/permissions';
import {
  paperSx,
  sectionTitleSx,
  pageContainerSx,
  headerSectionSx,
  loadingContainerSx,
  formSectionSx,
  contributionSectionSx,
  buttonContainerSx,
} from './styles';
import CreateEditShiftDrawer from './components/CreateEditShiftDrawer';
import DeleteShiftDialog from './components/DeleteShiftDialog';
import { TipShareShift } from '../types';
import TipShareContributionForm from './components/TipShareContributionForm';
import TipShareDistributionForm from './components/TipShareDistributionForm';
import TipShareShiftsSection from './components/TipShareShiftsSection';
import useShiftDrawerState from './hooks/useShiftDrawerState';
import useTipShareFormSync from './hooks/useTipShareFormSync';
import useDisableDistribution from './hooks/useDisableDistribution';
import useTipShareSave from './hooks/useTipShareSave';
import { TipShareFormData, DistributionType, AutoDistributionMethod } from './types';
import {
  useGetTipShareDistributionConfig,
  useGetTipShareContributionConfig,
  useGetTipShareShifts,
} from '../requests/queries';

// Validation schema factory function
const getValidationSchema = (t: (key: string, options?: { value?: number }) => string) =>
  yup.object({
    // Contribution fields
    enableTipShareContribution: yup.boolean(),
    contributionPercentage: yup.number().when('enableTipShareContribution', {
      is: true,
      then: (schema) =>
        schema
          .required(t('formMessage.required'))
          .min(0.01, t('formMessage.minValue', { value: 0.01 }))
          .max(100, t('formMessage.maxValue', { value: 100 }))
          .test(
            'max-two-decimals',
            t('formMessage.maxDecimalPlaces', { value: 2 }),
            (val) => val === undefined || val === null || /^\d+(\.\d{1,2})?$/.test(String(val))
          )
          .typeError(t('formMessage.validNumber')),
      otherwise: (schema) => schema.notRequired(),
    }),
    includeComps: yup.boolean(),
    includePromotions: yup.boolean(),
    includeTax: yup.boolean(),
    // Distribution fields
    enableTipShareDistribution: yup.boolean(),
    distributionType: yup.string().when('enableTipShareDistribution', {
      is: true,
      then: (schema) =>
        schema
          .required(t('formMessage.required'))
          .oneOf([DistributionType.AUTOMATIC, DistributionType.MANUAL], t('formMessage.required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    distributionMethod: yup
      .string()
      .nullable()
      .when('distributionType', {
        is: DistributionType.AUTOMATIC,
        then: (schema) => schema.required(t('formMessage.required')),
        otherwise: (schema) => schema.notRequired(),
      }),
    recalculateOnPosImport: yup.boolean(),
    autoBalanceGridDistribution: yup.boolean(),
  });

export default function TipShareSettingsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const user = useUsers();

  // Check user permissions
  const hasEditorRole = isPermitted(user, [CboRole.TIP_SHARE_CONFIG_EDIT]);
  const isReadOnly = !hasEditorRole;

  // Fetch tip share distribution, contribution, and shifts data
  const { distributionConfig, isDistributionLoading } = useGetTipShareDistributionConfig();
  const { contributionConfig, isContributionLoading } = useGetTipShareContributionConfig();
  const { data: shiftsRaw, isLoading: isLoadingShifts, isFetching: isFetchingShifts } = useGetTipShareShifts();
  const shiftsData: TipShareShift[] = Array.isArray(shiftsRaw) ? shiftsRaw : [];

  // Shift drawer/dialog — all related state in one reducer; single dispatch = single re-render
  const {
    selectedShift,
    isDrawerOpen: isShiftsDrawerOpen,
    isDeleteDialogOpen: isDeleteShiftDialogOpen,
    isEditing: isShiftEditing,
    isViewOnly: isShiftViewOnly,
    openCreate: handleShiftCreateClick,
    openView: handleShiftRowClick,
    openEdit: handleShiftEditClick,
    enterEditMode: handleShiftEnterEditMode,
    openDelete: handleShiftDeleteClick,
    close: handleShiftClose,
  } = useShiftDrawerState();

  // Memoize validation schema to prevent recreating it on every render
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const resolver = useYupValidationResolver(validationSchema);

  // Memoize default form values to prevent object recreation on every render
  const defaultValues = useMemo<TipShareFormData>(
    () => ({
      // Contribution defaults
      enableTipShareContribution: false,
      contributionPercentage: 0,
      includeComps: true,
      includePromotions: true,
      includeTax: true,
      // Distribution defaults
      enableTipShareDistribution: false,
      distributionType: '', // Empty string to prevent MUI RadioGroup from auto-selecting first option
      distributionMethod: '', // Empty string to prevent MUI RadioGroup from auto-selecting first option
      recalculateOnPosImport: false,
      autoBalanceGridDistribution: false,
    }),
    []
  );

  // Initialize form with default values
  const formContext = useForm<TipShareFormData>({
    resolver,
    defaultValues,
    mode: 'onBlur', // Only validate when field loses focus - won't show errors on programmatic changes
    reValidateMode: 'onChange', // After first blur, revalidate on every change
  });

  const { reset, formState, control, setValue } = formContext;
  const { isDirty } = formState;

  // Watch form fields - optimized with array destructuring
  const [
    enableTipShareDistribution,
    enableTipShareContribution,
    distributionType,
    contributionPercentage,
    distributionMethod,
  ] = useWatch({
    control,
    name: [
      'enableTipShareDistribution',
      'enableTipShareContribution',
      'distributionType',
      'contributionPercentage',
      'distributionMethod',
    ],
  });

  const { isInitialLoadComplete } = useTipShareFormSync({
    reset,
    setValue,
    distributionType: distributionType as DistributionType | '',
    distributionMethod: distributionMethod as AutoDistributionMethod | '',
    isDistributionLoading,
    isContributionLoading,
    distributionConfig,
    contributionConfig,
  });

  const { showDisableDialog, isDisabling, handleToggleChange, handleConfirmDisable, handleCancelDisable } =
    useDisableDistribution({
      distributionConfig,
      enableTipShareDistribution,
      reset,
      setValue,
    });

  const { isSaving, isSaveEnabled, saveTooltip, onSubmit } = useTipShareSave({
    distributionConfig,
    contributionConfig,
    shiftsData,
    enableTipShareDistribution,
    enableTipShareContribution,
    distributionType: distributionType as DistributionType | '',
    contributionPercentage,
    isDirty,
  });

  return (
    <>
      <FormContainer formContext={formContext} onSuccess={onSubmit}>
        <UnsavedFormDataPrompt
          when={isDirty && !isSaving}
          navigate={(path) => history.push(path)}
          shouldBlockNavigation={() => isDirty && !isSaving}
        />
        <Box data-testid='tip-share-page' sx={{ ...pageContainerSx, mr: isShiftsDrawerOpen ? '382px' : 4 }}>
          <Header title={t('navItems.tipShare')} dataTestId='tip-share-header' />
          <Box sx={headerSectionSx}>
            <Typography variant='body1' color='text.secondary'>
              {t('tipShare.description')}
            </Typography>
          </Box>

          {/* Show loading state only during initial load until both API calls complete */}
          {!isInitialLoadComplete ? (
            <Box sx={loadingContainerSx}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Tip-share distribution section */}
              <Box sx={formSectionSx}>
                <Paper elevation={0} sx={paperSx}>
                  <Typography variant='subtitle1' sx={sectionTitleSx} fontWeight={600}>
                    {t('tipShare.distribution.title')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enableTipShareDistribution}
                        onChange={handleToggleChange}
                        disabled={isReadOnly}
                        data-testid='enable-tip-share-distribution-switch'
                      />
                    }
                    label={t('tipShare.distribution.enableTipShareDistribution')}
                  />
                  {enableTipShareDistribution && (
                    <Box sx={contributionSectionSx}>
                      <TipShareDistributionForm isReadOnly={isReadOnly} />
                    </Box>
                  )}
                </Paper>
              </Box>

              {/* Tip-share shifts section */}
              {enableTipShareDistribution && (
                <TipShareShiftsSection
                  isAutomaticDistribution={distributionType === DistributionType.AUTOMATIC}
                  shiftsData={shiftsData}
                  isLoading={isLoadingShifts || isFetchingShifts}
                  isEditor={hasEditorRole}
                  onCreateClick={handleShiftCreateClick}
                  onRowClick={handleShiftRowClick}
                  onEditClick={handleShiftEditClick}
                  onDeleteClick={handleShiftDeleteClick}
                />
              )}

              {/* Tip-share contribution section */}
              {enableTipShareDistribution && (
                <Paper elevation={0} sx={paperSx}>
                  <Typography variant='subtitle1' sx={sectionTitleSx} fontWeight={600}>
                    {t('tipShare.contribution.title')}
                  </Typography>
                  {isContributionLoading ? (
                    <Box sx={buttonContainerSx}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <TipShareContributionForm isReadOnly={isReadOnly} />
                  )}
                </Paper>
              )}

              {/* Save and Cancel buttons - hide for read-only users */}
              {enableTipShareDistribution && !isReadOnly && (
                <FormFooter
                  loading={isSaving}
                  buttons={[
                    {
                      onClick: () => reset(),
                      dataTestId: 'cancel-button',
                      disabled: !isDirty,
                    },
                    {
                      dataTestId: 'save-button',
                      submitType: true,
                      disabled: !isSaveEnabled,
                      tooltip: saveTooltip || undefined,
                    },
                  ]}
                />
              )}
            </>
          )}
        </Box>

        {/* Disable Confirmation Dialog */}
        <ActionsDialog
          open={showDisableDialog}
          onClose={handleCancelDisable}
          dataTestId='disable-tip-share-dialog'
          dialogTitle={t('tipShare.disableDialog.title')}
          dialogContentText={t('tipShare.disableDialog.content')}
          closeButtonText={t('buttonText.cancel')}
          closeButtonVariant='outlined'
          actionButtonText={t('tipShare.disableDialog.confirmButton')}
          actionButtonColor='error'
          actionButtonVariant='contained'
          onActionClick={handleConfirmDisable}
          disableActionButton={isDisabling}
        />
      </FormContainer>

      {/* Shift drawer and delete dialog rendered outside FormContainer */}
      <CreateEditShiftDrawer
        open={isShiftsDrawerOpen}
        onClose={handleShiftClose}
        shift={selectedShift}
        existingShifts={shiftsData}
        isEditing={isShiftEditing}
        readOnly={isShiftViewOnly}
        onEnterEditMode={hasEditorRole ? handleShiftEnterEditMode : undefined}
      />
      <DeleteShiftDialog open={isDeleteShiftDialogOpen} onClose={handleShiftClose} shift={selectedShift} />
    </>
  );
}
